import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { relaunch } from '@tauri-apps/plugin-process';
import { check } from '@tauri-apps/plugin-updater';
import { isDesktopTauri } from '../../plugins/useTauriOpener';
import { useSetting } from '../hooks/settings';
import { settingsAtom } from '../settings';

type UpdateProgress = {
  downloaded: number;
  total: number;
};

type UpdateState = {
  updateAvailable: boolean;
  updateDownloaded: boolean;
  downloading: boolean;
  checking: boolean;
  checked: boolean;
  version: string | null;
  body: string | null;
  progress: UpdateProgress | null;
  error: string | null;
};

type UpdateCheckerContextValue = UpdateState & {
  checkAndPrepare: () => Promise<void>;
  applyUpdate: () => Promise<void>;
};

const initial: UpdateState = {
  updateAvailable: false,
  updateDownloaded: false,
  downloading: false,
  checking: false,
  checked: false,
  version: null,
  body: null,
  progress: null,
  error: null,
};

const UpdateCheckerContext = React.createContext<UpdateCheckerContextValue>({
  ...initial,

  checkAndPrepare: async () => {},

  applyUpdate: async () => {},
});

export function useUpdateChecker() {
  return useContext(UpdateCheckerContext);
}

// Simple event emitter so the SettingsTab can listen for "open about" requests.
type OpenAboutListener = () => void;
const openAboutListeners = new Set<OpenAboutListener>();

export function onOpenAbout(listener: OpenAboutListener): () => void {
  openAboutListeners.add(listener);
  return () => {
    openAboutListeners.delete(listener);
  };
}

function emitOpenAbout() {
  openAboutListeners.forEach((fn) => fn());
}

export function UpdateCheckerProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<UpdateState>(initial);
  const [autoUpdateCheck] = useSetting(settingsAtom, 'autoUpdateCheck');
  const checkingRef = useRef(false);
  const pendingUpdateRef = useRef<Awaited<ReturnType<typeof check>> | null>(null);

  const checkAndPrepare = useCallback(async () => {
    if (!isDesktopTauri || checkingRef.current) return;
    checkingRef.current = true;

    try {
      setState((s) => ({ ...s, checking: true, checked: false, error: null }));
      const update = await check();

      if (!update) {
        setState((s) => ({
          ...s,
          checking: false,
          checked: true,
          updateAvailable: false,
          updateDownloaded: false,
          downloading: false,
          error: null,
        }));
        checkingRef.current = false;
        return;
      }

      const { version } = update;
      const body = update.body ?? null;

      const isWindows = navigator.userAgent.includes('Windows');

      if (isWindows) {
        // On Windows, do NOT auto-download. Just notify the user that an update is available.
        // The user will confirm, then download + install + restart all at once.
        pendingUpdateRef.current = update;
        setState((s) => ({
          ...s,
          checking: false,
          updateAvailable: true,
          version,
          body,
        }));
        return;
      }

      setState((s) => ({
        ...s,
        checking: false,
        updateAvailable: true,
        version,
        body,
        downloading: true,
      }));

      let downloaded = 0;
      let total = 0;

      const onEvent = (
        event:
          | { event: 'Started'; data: { contentLength?: number } }
          | { event: 'Progress'; data: { chunkLength: number } }
          | { event: 'Finished' },
      ) => {
        switch (event.event) {
          case 'Started':
            total = event.data.contentLength ?? 0;
            break;
          case 'Progress':
            downloaded += event.data.chunkLength;
            setState((s) => ({
              ...s,
              progress: { downloaded, total },
            }));
            break;
          default:
            break;
        }
      };

      await update.downloadAndInstall(onEvent);

      setState((s) => ({
        ...s,
        downloading: false,
        updateDownloaded: true,
        progress: { downloaded, total },
      }));
    } catch (e) {
      setState((s) => ({
        ...s,
        checking: false,
        downloading: false,
        error: e instanceof Error ? e.message : String(e),
      }));
    } finally {
      checkingRef.current = false;
    }
  }, []);

  const applyUpdate = useCallback(async () => {
    if (!isDesktopTauri) return;
    const pendingUpdate = pendingUpdateRef.current;
    if (pendingUpdate) {
      // Windows: download + install + restart all at once after user confirmation.
      setState((s) => ({ ...s, downloading: true }));

      let downloaded = 0;
      let total = 0;

      await pendingUpdate.downloadAndInstall((event) => {
        switch (event.event) {
          case 'Started':
            total = event.data.contentLength ?? 0;
            break;
          case 'Progress':
            downloaded += event.data.chunkLength;
            setState((s) => ({
              ...s,
              progress: { downloaded, total },
            }));
            break;
          default:
            break;
        }
      });

      pendingUpdateRef.current = null;
      setState((s) => ({
        ...s,
        downloading: false,
        updateDownloaded: true,
        progress: { downloaded, total },
      }));
    }
    await relaunch();
  }, []);

  // Auto-check for updates on startup and every 4 hours.
  useEffect(() => {
    if (!isDesktopTauri || !autoUpdateCheck) return;

    const timer = setTimeout(() => {
      checkAndPrepare();
    }, 5000);

    const interval = setInterval(
      () => {
        checkAndPrepare();
      },
      4 * 60 * 60 * 1000,
    );

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [checkAndPrepare, autoUpdateCheck]);

  // Sync update menu state with Tauri when state changes.
  useEffect(() => {
    if (!isDesktopTauri) return;

    const ready =
      state.updateDownloaded || (state.updateAvailable && !state.downloading && !state.error);
    const disabled = state.checking || state.downloading;
    invoke('update_menu_state', {
      apply: ready,
      downloaded: state.updateDownloaded,
      disabled,
    });
  }, [
    state.checking,
    state.downloading,
    state.updateAvailable,
    state.updateDownloaded,
    state.error,
  ]);

  // Listen for "check-for-updates" / "apply-update" events from Rust (menu click).
  useEffect(() => {
    if (!isDesktopTauri) return;

    let cancelled = false;
    const unlistenPromise = listen<{ openSettings: boolean }>('check-for-updates', (event) => {
      if (cancelled) return;
      if (event.payload.openSettings) {
        emitOpenAbout();
      }
      checkAndPrepare();
    });

    const unlistenApplyPromise = listen<{ openSettings: boolean }>('apply-update', (event) => {
      if (cancelled) return;
      if (event.payload.openSettings) {
        emitOpenAbout();
      }
      applyUpdate();
    });

    return () => {
      cancelled = true;
      unlistenPromise.then((unlisten) => {
        if (cancelled) unlisten();
      });
      unlistenApplyPromise.then((unlisten) => {
        if (cancelled) unlisten();
      });
    };
  }, [checkAndPrepare, applyUpdate]);

  const value = useMemo<UpdateCheckerContextValue>(
    () => ({
      ...state,
      checkAndPrepare,
      applyUpdate,
    }),
    [checkAndPrepare, applyUpdate, state],
  );

  return <UpdateCheckerContext.Provider value={value}>{children}</UpdateCheckerContext.Provider>;
}
